/* Copyright (c) 2018-2026, NVIDIA CORPORATION.  All rights reserved.
 *
 * NVIDIA CORPORATION and its licensors retain all intellectual property
 * and proprietary rights in and to this software, related documentation
 * and any modifications thereto.  Any use, reproduction, disclosure or
 * distribution of this software and related documentation without an express
 * license agreement from NVIDIA CORPORATION is strictly prohibited.
 */
import { Observable, Subject } from 'rxjs';
import { IDBPDatabase } from 'idb';
import { DbCache as DbCacheImport } from './db-cache.class';
import { DbDataStore } from './db-data-store.class';

/* Common */

export interface DbItemMetadeta {
    storeName: string;
    userId: string;
    key: string;
}

/* DbDataStore and DbService */

export interface DbStoreDefault {
    version: number;
    db: {
        [key: string]: any;
    };
}

export interface DbConfig {
    name?: string;
    storeName: string;
    storeDefault?: DbStoreDefault;
    description?: string;
}

export type DbUpgradeCallback = (dataStore: DbDataStore) => Observable<any>;

export type DbUpgradeMap = Map<number, DbUpgradeCallback>;

export interface DbStoreDefinition {
    config: DbConfig;
    upgrades: DbUpgradeMap;
    storeDefault: DbStoreDefault;
    upgradeTargetVersion?: number;
}

export type DbStoreDefinitionMap = Map<string, DbStoreDefinition>;

export interface DbDataStoreConfig {
    dbName?: string;
    storeName: string;
    storeDbName: string;
    storeDefinition?: DbStoreDefinition;
    userId?: string;
}

export type DbDataStoreMap = Map<string, DbDataStore>;

/* DbCache and DbCacheService */

export enum DbCacheStatus {
    Loading = 'Loading',
    Dirty = 'Dirty',
    Saved = 'Saved',
    NotFound = 'NotFound'
}

export type DbCacheSyncObserverFunction = () => any;

export type DbCacheSyncObservers = Set<DbCacheSyncObserverFunction>;

export type DbCacheDefinitionMap = Map<string, Set<string>>;

export type DbCacheStoreMap = Map<string, DbCacheImport>;

export type DbCacheMap = Map<string, DbCacheStoreMap>;

export type DbStore = DbDataStore;

export type DbCache = DbCacheImport;

export interface DbRequest {
    workerFn?: Observable<IDBPDatabase>;
    readonly response$: Subject<IDBPDatabase>;
}

export enum DbPerformanceMarks {
    UpgradeStart = 'UpgradeStart',
    UpgradeEnd = 'UpgradeEnd'
}

export enum DbPerformanceMeasures {
    Upgrade = 'Upgrade'
}
